import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  collection, documentId, getDocsFromServer, limit, onSnapshot, orderBy, query, startAfter, where,
} from "firebase/firestore";

export const useBatchedOrders = ({ db, roundDate, status, batchSize, search, currentPage = 1, pageSize = 50 }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState("");
  const [isLive, setIsLive] = useState(false);
  const pageRef = useRef(currentPage);
  pageRef.current = currentPage;
  const [revision, setRevision] = useState(0);
  const requestRef = useRef({ busy: false, cursor: null, hasMore: false });
  const ordersQuery = useMemo(() => {
    if (!db || !roundDate) return null;
    return query(
      collection(db, "Orders"),
      where("roundDate", "==", roundDate),
      ...(status ? [where("status", "==", status)] : []),
      ...(search ? [where(search.field, "==", search.value)] : []),
      orderBy("createdAt", "desc"),
      orderBy(documentId(), "desc"),
    );
  }, [db, roundDate, status, search]);

  const readBatch = useCallback(async (append, request) => {
    if (!ordersQuery || request.busy || (append && !request.hasMore)) return false;
    request.busy = true;
    setError("");
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const snapshot = await getDocsFromServer(query(
        ordersQuery,
        ...(append && request.cursor ? [startAfter(request.cursor)] : []),
        limit(batchSize + 1),
      ));
      if (requestRef.current !== request) return false;
      const docs = snapshot.docs.slice(0, batchSize);
      const nextOrders = docs.map(doc => {
        const data = doc.data();
        return { ...data, id: doc.id, slips: data.slips?.at(-1) ?? null };
      });
      request.cursor = docs.at(-1) || request.cursor;
      request.hasMore = snapshot.docs.length > batchSize;
      setHasMore(request.hasMore);
      setOrders(previous => {
        if (!append) return nextOrders;
        const seenIds = new Set(previous.map(order => order.id));
        return [...previous, ...nextOrders.filter(order => !seenIds.has(order.id))];
      });
      return docs.length > 0;
    } catch (err) {
      if (requestRef.current === request) {
        console.error("orders batch query error:", err);
        setError(append ? "โหลดรายการถัดไปไม่สำเร็จ กรุณากดถัดไปเพื่อลองอีกครั้ง" : "โหลดคำสั่งซื้อไม่สำเร็จ กรุณากดโหลดรายการล่าสุดเพื่อลองอีกครั้ง");
      }
      return false;
    } finally {
      if (requestRef.current === request) {
        request.busy = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [ordersQuery, batchSize]);

  useEffect(() => {
    const request = { busy: false, cursor: null, hasMore: false };
    requestRef.current = request;
    setOrders([]);
    setHasMore(false);
    setError("");
    setLoadingMore(false);
    if (ordersQuery && pageRef.current !== 1) readBatch(false, request);
    else if (ordersQuery) setLoading(true);
    else setLoading(false);
    return () => { requestRef.current.stopLive?.(); requestRef.current = {}; };
  }, [ordersQuery, readBatch, revision]);

  const live = currentPage === 1;
  useEffect(() => {
    if (!ordersQuery || !live) return;
    // Returning to page one starts a new pagination session. Never combine a
    // moving live boundary with cached pages from an older cursor.
    const request = { busy: true, cursor: null, hasMore: false };
    requestRef.current = request;
    setOrders([]);
    setHasMore(false);
    setLoading(true);
    setLoadingMore(false);
    setError("");
    let active = true;
    const unsubscribe = onSnapshot(query(ordersQuery, limit(pageSize + 1)), snapshot => {
      if (!active || requestRef.current !== request) return;
      const docs = snapshot.docs.slice(0, pageSize);
      request.cursor = docs.at(-1) || null;
      request.hasMore = snapshot.docs.length > pageSize;
      request.busy = false;
      setIsLive(true);
      setOrders(docs.map(doc => {
        const data = doc.data();
        return { ...data, id: doc.id, slips: data.slips?.at(-1) ?? null };
      }));
      setHasMore(request.hasMore);
      setLoading(false);
      setError("");
    }, err => {
      if (!active || requestRef.current !== request) return;
      console.error("orders live query error:", err);
      request.busy = false;
      setIsLive(false);
      setLoading(false);
      setError("เชื่อมต่อรายการสดไม่สำเร็จ กรุณากดโหลดรายการล่าสุดเพื่อลองอีกครั้ง");
    });
    request.stopLive = () => {
      if (!active) return;
      active = false;
      setIsLive(false);
      unsubscribe();
    };
    return request.stopLive;
  }, [ordersQuery, batchSize, revision, live, pageSize]);

  const loadMore = useCallback(() => {
    const request = requestRef.current;
    if (request.busy || !request.hasMore) return Promise.resolve(false);
    request.stopLive?.();
    return readBatch(true, request);
  }, [readBatch]);
  const refresh = useCallback(() => {
    requestRef.current.stopLive?.();
    requestRef.current = {};
    setRevision(value => value + 1);
  }, []);
  return { orders, isLive, loading, loadingMore, hasMore, error, loadMore, refresh };
};
